import React, { useState } from "react";
import * as XLSX from "xlsx";

// Normal endpoints
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetExpensesQuery } from "../../slices/expensesApiSlice";
import { useGetIncomesQuery } from "../../slices/incomesApiSlice";
import { useGetDepositsQuery } from "../../slices/depositsApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";

// Store endpoints
import { useGetStoreSalesQuery } from "../../slices/storeSalesApiSlice";
import { useGetStoreExpensesQuery } from "../../slices/storeExpensesApiSlice";
import { useGetStoreIncomesQuery } from "../../slices/storeIncomesApiSlice";
import { useGetStoreDepositsQuery } from "../../slices/storeDepositsApiSlice";
import { useGetStoreSuppliesQuery } from "../../slices/storeSuppliesApiSlice";

export default function ConsolidatedBalance() {
  const today = new Date().toISOString().split("T")[0];

  // Normal data
  const { data: salesData } = useGetSalesQuery();
  const { data: expensesData } = useGetExpensesQuery({ startDate: today, endDate: today });
  const { data: incomesData } = useGetIncomesQuery({ startDate: today, endDate: today });
  const { data: depositsData } = useGetDepositsQuery({ startDate: today, endDate: today });
  const { data: suppliesData } = useGetSuppliesQuery();

  // Store data
  const { data: storeSalesData } = useGetStoreSalesQuery();
  const { data: storeExpensesData } = useGetStoreExpensesQuery({ startDate: today, endDate: today });
  const { data: storeIncomesData } = useGetStoreIncomesQuery({ startDate: today, endDate: today });
  const { data: storeDepositsData } = useGetStoreDepositsQuery({ startDate: today, endDate: today });
  const { data: storeSuppliesData } = useGetStoreSuppliesQuery();

  const [date1, setDate1] = useState(today);
  const [date2, setDate2] = useState(today);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState({
    totalCSale: 0,
    totalCExpense: 0,
    totalCIncome: 0,
    totalCDeposit: 0,
    totalCPurchases: 0,
    totalCCashInHand: 0,
    totalCNetCash: 0,
    totalCDifference: 0,
  });

  // Generate dates
  const getDatesBetween = (startDate, endDate) => {
    const dates = [];
    let current = new Date(startDate);

    while (current <= new Date(endDate)) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  // Calculate consolidated report
  const calculateReport = () => {
    const dates = getDatesBetween(date1, date2);

    const result = dates.map((date) => {
      // Normal
      const netSales =
        salesData?.sales
          ?.filter((x) => x.date && new Date(x.date).toISOString().split("T")[0] === date)
          .reduce((sum, x) => sum + Number(x.totalPrice || 0), 0) || 0;

      const netExpenses =
        expensesData?.expenses
          ?.filter((x) => x.date && new Date(x.date).toISOString().split("T")[0] === date)
          .reduce((sum, x) => sum + Number(x.amount || 0), 0) || 0;

      const cashCollected =
        incomesData?.incomes
          ?.filter((x) => x.date && new Date(x.date).toISOString().split("T")[0] === date)
          .reduce((sum, x) => sum + Number(x.amount || 0), 0) || 0;

      const depositTotal =
        depositsData?.deposits
          ?.filter((x) => x.date && new Date(x.date).toISOString().split("T")[0] === date)
          .reduce((sum, x) => sum + Number(x.amount || 0), 0) || 0;

      const netBank = depositTotal > 0 ? depositTotal * -1 : depositTotal;

      const purchases =
        (suppliesData?.supplies
          ?.filter((x) => x.date && new Date(x.date).toISOString().split("T")[0] === date)
          .reduce((sum, x) => sum + Number(x.cardCost || 0), 0) || 0) * -1;

      // Store
      const netStoreSales =
        storeSalesData?.storeSales
          ?.filter((x) => x.date && new Date(x.date).toISOString().split("T")[0] === date)
          .reduce((sum, x) => sum + Number(x.totalPrice || 0), 0) || 0;

      const netStoreExpenses =
        storeExpensesData?.storeExpenses
          ?.filter((x) => x.date && new Date(x.date).toISOString().split("T")[0] === date)
          .reduce((sum, x) => sum + Number(x.amount || 0), 0) || 0;

      const storeCashCollected =
        storeIncomesData?.storeIncomes
          ?.filter((x) => x.date && new Date(x.date).toISOString().split("T")[0] === date)
          .reduce((sum, x) => sum + Number(x.amount || 0), 0) || 0;

      const storeDepositTotal =
        storeDepositsData?.storeDeposits
          ?.filter((x) => x.date && new Date(x.date).toISOString().split("T")[0] === date)
          .reduce((sum, x) => sum + Number(x.amount || 0), 0) || 0;

      const storeNetBank = storeDepositTotal > 0 ? storeDepositTotal * -1 : storeDepositTotal;

      const storePurchases =
        (storeSuppliesData?.storeSupplies
          ?.filter((x) => x.date && new Date(x.date).toISOString().split("T")[0] === date)
          .reduce((sum, x) => sum + Number(x.cardCost || 0), 0) || 0) * -1;

      // Consolidated
      const CSale = netSales + netStoreSales;
      const CExpense = netExpenses + netStoreExpenses;
      const CIncome = cashCollected + storeCashCollected;
      const CDeposit = netBank + storeNetBank;
      const CPurchases = purchases + storePurchases;

      const CNetCash = CSale + CExpense;
      const CDifference = CIncome - CNetCash;
      const CCashInHand = CNetCash + CDeposit + CPurchases;

      return {
        date,
        CSale,
        CExpense,
        CNetCash,
        CIncome,
        CDifference,
        CDeposit,
        CPurchases,
        CCashInHand,
      };
    });

    setReportData(result);
    calculateTotals(result);
  };

  // Totals
  const calculateTotals = (data) => {
    const totalCSale = data.reduce((s, r) => s + r.CSale, 0);
    const totalCExpense = data.reduce((s, r) => s + r.CExpense, 0);
    const totalCIncome = data.reduce((s, r) => s + r.CIncome, 0);
    const totalCDeposit = data.reduce((s, r) => s + r.CDeposit, 0);
    const totalCPurchases = data.reduce((s, r) => s + r.CPurchases, 0);
    const totalCCashInHand = data.reduce((s, r) => s + r.CCashInHand, 0);
    const totalCNetCash = totalCSale + totalCExpense;
    const totalCDifference = totalCIncome - totalCNetCash;

    setTotals({
      totalCSale: totalCSale.toFixed(2),
      totalCExpense: totalCExpense.toFixed(2),
      totalCIncome: totalCIncome.toFixed(2),
      totalCDeposit: totalCDeposit.toFixed(2),
      totalCPurchases: totalCPurchases.toFixed(2),
      totalCCashInHand: totalCCashInHand.toFixed(2),
      totalCNetCash: totalCNetCash.toFixed(2),
      totalCDifference: totalCDifference.toFixed(2),
    });
  };

  // Excel export
  const exportToExcel = () => {
    const exportData = reportData.map((row) => ({
      Date: row.date,
      CSale: row.CSale.toFixed(2),
      CExpense: row.CExpense.toFixed(2),
      CNetCash: row.CNetCash.toFixed(2),
      CIncome: row.CIncome.toFixed(2),
      CDifference: row.CDifference.toFixed(2),
      CDeposit: row.CDeposit.toFixed(2),
      CPurchases: row.CPurchases.toFixed(2),
      CCashInHand: row.CCashInHand.toFixed(2),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Consolidated Balance");
    XLSX.writeFile(wb, "ConsolidatedBalance.xlsx");
  };

  return (
    <div className="balance-report">
      <h1>Consolidated Balance Report</h1>

      <div className="date-range">
        <label>
          Start Date:
          <input type="date" value={date1} onChange={(e) => setDate1(e.target.value)} />
        </label>
        <label>
          End Date:
          <input type="date" value={date2} onChange={(e) => setDate2(e.target.value)} />
        </label>
      </div>

      <button onClick={calculateReport} className="calculate-button">
        Calculate Report
      </button>

      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>CSale</th>
            <th>CExpense</th>
            <th>CNetCash</th>
            <th>CIncome</th>
            <th>CDifference</th>
            <th>CDeposit</th>
            <th>CPurchases</th>
            <th>CCashInHand</th>
          </tr>
        </thead>

        <tbody>
          {reportData.map((row) => (
            <tr key={row.date}>
              <td>{row.date}</td>
              <td>{row.CSale.toFixed(2)}</td>
              <td>{row.CExpense.toFixed(2)}</td>
              <td>{row.CNetCash.toFixed(2)}</td>
              <td>{row.CIncome.toFixed(2)}</td>
              <td>{row.CDifference.toFixed(2)}</td>
              <td>{row.CDeposit.toFixed(2)}</td>
              <td>{row.CPurchases.toFixed(2)}</td>
              <td>{row.CCashInHand.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>

        <tfoot>
          <tr>
            <td><strong>Total</strong></td>
            <td><strong>{totals.totalCSale}</strong></td>
            <td><strong>{totals.totalCExpense}</strong></td>
            <td><strong>{totals.totalCNetCash}</strong></td>
            <td><strong>{totals.totalCIncome}</strong></td>
            <td><strong>{totals.totalCDifference}</strong></td>
            <td><strong>{totals.totalCDeposit}</strong></td>
            <td><strong>{totals.totalCPurchases}</strong></td>
            <td><strong>{totals.totalCCashInHand}</strong></td>
          </tr>
        </tfoot>
      </table>

      <button onClick={exportToExcel} className="export-button">
        Export to Excel
      </button>
    </div>
  );
}
